import React, { useEffect, useRef, useState } from 'react';
import { CloseIcon, CollapseIcon, MenuIcon } from '../common/icon';
import NavbarContent from './NavbarContent';
import ModalBox from './ModalBox';
import { useInfluencerInfo } from '@/hooks/useInfluencerInfo';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import { usePlatformStore } from '@/hooks/usePlatformStore';
import { useSettingsStore } from '@/hooks/useSettingsStore';
import { useBaselineConfig } from '@/hooks/useBaselineConfig';
import {
  clearStoredLeaderboardShareId,
  getHomeUrl,
  getKeyFromUrlSearch,
  getStoredLeaderboardShareId,
  isMobile,
  isNativeAppRuntime,
  localStorageSaveUtmMediumAndSource,
  maskEmail,
  notMatch
} from '@/biz/tool';
import axiosInstance from '@/apis/axiosInstance';
import { useLoginStore } from '@/hooks/useLoginStore';
import {
  getUserLang,
  handleLangConflict,
  normalizeToSupportedLang
} from '@/i18n/utils';
import { useLocation } from 'react-router-dom';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Drawer from '@material-ui/core/Drawer';
import { Tooltip } from 'aha-components';
import { toggleNativeVConsole } from '@/app/debug/vconsole';
import { isNonProductionEnv } from '@/utils/getEnv';
import { useTimModalStore } from '@/tim/useTimModalStore';

const shouldShowNativeDevLabel = () => {
  if (typeof window === 'undefined') return false;
  return isNativeAppRuntime() && isNonProductionEnv();
};

const isTestEnvironment = () =>
  typeof window !== 'undefined' && isNonProductionEnv();

const NativeDevLabel = () => (
  <span className="ml-2 shrink-0 text-[11px] font-semibold leading-none text-[#BE1E2D]">
    [dev]
  </span>
);

function NavbarLogo({
  enableDebug,
  showNativeDevLabel,
  onLogoIconClick,
  compact = false
}: {
  enableDebug: boolean;
  showNativeDevLabel: boolean;
  onLogoIconClick: () => void;
  compact?: boolean;
}) {
  return (
    <div
      className={`relative flex min-w-0 items-center ${
        compact ? 'h-8 w-8 overflow-hidden' : ''
      }`}
    >
      <img
        alt="logo"
        src="https://website-resource.ahaglobal.io/aha-brand-logo/aha-logo-name-black.png"
        className={`max-w-none shrink-0 ${
          compact ? 'h-8 w-[173px]' : 'w-[173px]'
        }`}
      />
      {enableDebug && (
        <button
          type="button"
          className="absolute left-0 top-1/2 h-10 w-10 -translate-y-1/2 cursor-pointer border-0 bg-transparent p-0 outline-none"
          aria-label="Open debug tools"
          onClick={onLogoIconClick}
        />
      )}
      {showNativeDevLabel && <NativeDevLabel />}
    </div>
  );
}

export default function NewNavbar({
  guestMode = false
}: {
  guestMode?: boolean;
}) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const { refresh: fetchInfluencerInfo } = useInfluencerInfo();
  const {
    closeModalEmailVerification,
    showContactInformationModal,
    setShowModalEmailVerification,
    setFirstApplyVerifyEmailVerified,
    setHasAppliedOrder
  } = useWebModalStore();
  const { setInfluencers, setUser } = usePlatformStore();
  const { setCollaborationConfig } = useSettingsStore();
  const { setBaselineConfig } = useBaselineConfig();
  const router = useLocation();
  const { detailData } = useDetailDataStore();
  const pathname = window.location.pathname;
  const { setChannelAvatar, setChannelName, setUserEmail, setUserAddressInfo } =
    useLoginStore();
  // 这里强制 sync 到 localStorage，例如有非法值或空值
  const initUserLang = getUserLang(true).detectedValue;

  const toggleCollapsed = () => {
    setCollapsed((prev) => !prev);
  };
  const debugLogoTapRef = useRef<{
    count: number;
    timer: number | null;
  }>({ count: 0, timer: null });

  const handleLogoClick = () => {
    if (!isTestEnvironment()) return;

    const tapState = debugLogoTapRef.current;
    tapState.count += 1;
    if (tapState.timer) {
      window.clearTimeout(tapState.timer);
    }

    if (tapState.count >= 2) {
      tapState.count = 0;
      tapState.timer = null;
      void toggleNativeVConsole();
      return;
    }

    tapState.timer = window.setTimeout(() => {
      tapState.count = 0;
      tapState.timer = null;
    }, 500);
  };

  const sidebarClassName = collapsed
    ? "relative hidden h-screen min-h-0 w-[var(--creator-sidebar-collapsed-width)] flex-shrink-0 flex-col overflow-hidden bg-[var(--creator-sidebar-background)] transition-[width] duration-300 after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-[var(--creator-sidebar-border)] after:content-[''] md:flex"
    : "relative hidden h-screen min-h-0 w-[var(--creator-sidebar-expanded-width)] flex-shrink-0 flex-col overflow-hidden bg-[var(--creator-sidebar-background)] transition-[width] duration-300 after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-[var(--creator-sidebar-border)] after:content-[''] md:flex";

  const getLeaderboardShareIdToSend = () =>
    getKeyFromUrlSearch('leaderboardShareId') || getStoredLeaderboardShareId();

  const isRecentSignupUser = (createdAt?: string | Date) => {
    if (!createdAt) return false;
    const createdAtTime = new Date(createdAt).getTime();
    if (!Number.isFinite(createdAtTime)) return false;
    return Date.now() - createdAtTime <= 24 * 60 * 60 * 1000;
  };

  const recordLeaderboardShareSignup = async (shareId: string) => {
    if (!shareId) return;
    try {
      await axiosInstance.post('/leaderboard/api/share/signup', {
        shareId
      });
      clearStoredLeaderboardShareId();
    } catch (error) {
      console.error('recordLeaderboardShareSignup error: ', error);
    }
  };

  const refreshCreatorInfo = async () => {
    const pathname = window.location.pathname;
    const search = window.location.search;
    const current = `${pathname}${search}`;
    try {
      const payload = await fetchInfluencerInfo();
      if (!payload) return;

      const { influencers, user } = payload;
      // 保存是否申请订单&验证邮件状态； 申请过订单 但是还没有验证邮件，则弹出验证邮件弹窗
      setFirstApplyVerifyEmailVerified(user?.firstApplyVerifyEmailVerified);
      setHasAppliedOrder(user?.hasAppliedOrder);
      if (
        !user?.firstApplyVerifyEmailVerified &&
        user.hasAppliedOrder &&
        !closeModalEmailVerification
      ) {
        setShowModalEmailVerification(true);
      }
      // console.log(window.location.pathname.includes('detail'));
      if (influencers) {
        setInfluencers(influencers);
      }
      if (user?.collaborationConfig) {
        setCollaborationConfig(user?.collaborationConfig);
      }
      if (user?.baselineConfig) {
        setBaselineConfig(user?.baselineConfig);
      }
      if (influencers && !influencers?.length) {
        const leaderboardShareId = getLeaderboardShareIdToSend();
        if (leaderboardShareId) {
          if (isRecentSignupUser(user?.createdAt)) {
            await recordLeaderboardShareSignup(leaderboardShareId);
          } else {
            clearStoredLeaderboardShareId();
          }
        }
        // if (pathname === '/') {
        //   // 没找到达人信息， 需要跳转到达人连接页面
        //   window.location.replace('/connect');
        // }
      } else if (influencers?.length) {
        clearStoredLeaderboardShareId();
      }
      setUser(user);
      const info: any = influencers?.[0] || {};

      // 设置info, 登录没授权则没有
      if (info) {
        setChannelAvatar(info?.profilePictureLink);
        setChannelName(info?.name);
      }
      // 设置邮箱，登录就有
      if (user) {
        setUserEmail(user?.email);
        setUserAddressInfo(user?.addressInfo);
        // 如果 API 获取的 lang 和检测(ls)出来的不一致，则重写缓存并刷新。前提是接口下发值为默认值
        if (user.lang && user.lang !== 'auto') {
          const apiLang = normalizeToSupportedLang(user.lang);
          if (apiLang !== initUserLang) {
            handleLangConflict(user.lang, initUserLang);
          } else if (user.lang !== apiLang) {
            axiosInstance
              .post('/influencer/info', {
                lang: apiLang
              })
              .catch(() => {
                /* eat errors */
              });
          }
        } else {
          // 是默认值，静默发个请求覆盖掉
          axiosInstance
            .post('/influencer/info', {
              lang: initUserLang
            })
            .catch(() => {
              /* eat errors */
            });
        }
      }
    } catch (error) {
      console.error('Influencer information query error:', error);
      const target = getHomeUrl({ showLoginModal: true });
      if (
        !current.includes('detail') &&
        !current.includes('connect') &&
        !current.includes('contactInfo') &&
        !current.includes('/?showLoginModal=1') &&
        !current.includes('/callback') &&
        !current.includes('/error') &&
        !current.includes('/loginError') &&
        current !== target
      ) {
        // window.location.href = getHomeUrl({ showLoginModal: true });
      }
      // 存储 utm_medium
      // 存储 utm_source
      localStorageSaveUtmMediumAndSource();
      //
      if (current.includes('/?showLoginModal=1')) {
        // 清除url中的loginSuccess参数
        window.history.pushState({}, '', getHomeUrl());
      }
    }
  };

  // 监听路由变化
  useEffect(() => {
    // 全局整体鉴权
    if (guestMode) return;
    refreshCreatorInfo();
  }, [guestMode, router]);

  useEffect(() => {
    if (guestMode) return;
    // 这里的 showContactInformationModal 是当前值
    return () => {
      // 这里的 showContactInformationModal 是变化前的值
      if (showContactInformationModal === true) {
        // 说明从 true 变为 false
        // 刷新一下 info
        refreshCreatorInfo();
      }
    };
  }, [guestMode, showContactInformationModal]);

  useEffect(() => {
    // 仅在移动端时，当 detailData 更新时滚动到顶部
    if (isMobile()) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
    if (detailData?.user && pathname.includes('detail')) {
      const isNotMatch = notMatch({ detailData });
      if (isNotMatch) {
        setTimeout(() => {
          if (detailData?.order?.user?.email) {
            toast.error(
              t('common.social_account_linked_email', {
                defaultValue:
                  'This social account is already linked to {{email}}. Please sign in with this email, or contact support if you need to update it.',
                email: maskEmail(detailData?.order?.user?.email)
              })
            );
          } else {
            toast.error(
              t('common.social_account_linked', {
                defaultValue:
                  'Your social account is linked to a different email address. Please use that email to sign in to the platform. If you have any further questions, feel free to contact our support team.'
              })
            );
          }
        }, 0);
      }
    }
  }, [detailData]);

  const [menuOpen, setMenuOpen] = useState(false);
  const showNativeDevLabel = shouldShowNativeDevLabel();
  const enableNavbarDebug = isTestEnvironment();
  const { timUnreadCount } = useTimModalStore();
  return (
    <>
      <div
        className={sidebarClassName}
        data-testid="creator-sidebar"
        data-collapsed={collapsed}
      >
        <div className="flex h-20 flex-shrink-0 items-start justify-center px-4 pt-6">
          {collapsed ? (
            <div className="group relative h-8 w-8">
              <div className="transition-opacity duration-200 group-hover:opacity-0 group-focus-within:opacity-0">
                <NavbarLogo
                  compact
                  enableDebug={enableNavbarDebug}
                  showNativeDevLabel={false}
                  onLogoIconClick={handleLogoClick}
                />
              </div>
              <button
                type="button"
                className="absolute inset-0 flex h-8 w-8 items-center justify-center rounded-md border border-[var(--creator-sidebar-border)] bg-white opacity-0 shadow-sm transition-opacity duration-200 hover:bg-[var(--creator-sidebar-surface)] focus:opacity-100 group-hover:opacity-100"
                aria-label={t('navbar.menu.expand', {
                  defaultValue: 'Open the menu'
                })}
                aria-expanded={false}
                onClick={toggleCollapsed}
              >
                <CollapseIcon className="h-4 w-4 text-[var(--creator-sidebar-icon)]" />
              </button>
            </div>
          ) : (
            <div className="flex h-8 w-full items-center justify-between">
              <NavbarLogo
                enableDebug={enableNavbarDebug}
                showNativeDevLabel={showNativeDevLabel}
                onLogoIconClick={handleLogoClick}
              />
              <Tooltip
                content={t('navbar.menu.collapse', {
                  defaultValue: 'Close the menu'
                })}
              >
                <button
                  type="button"
                  className="flex h-7 w-7 items-center justify-center rounded-md border border-[var(--creator-sidebar-border)] bg-white shadow-sm transition-colors duration-200 hover:bg-[var(--creator-sidebar-surface)]"
                  aria-label={t('navbar.menu.collapse', {
                    defaultValue: 'Close the menu'
                  })}
                  aria-expanded
                  onClick={toggleCollapsed}
                >
                  <CollapseIcon className="h-4 w-4 text-[var(--creator-sidebar-icon)]" />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
        <div className="transition-all duration-300 flex-1 min-h-0 flex flex-col overflow-hidden">
          <NavbarContent collapsed={collapsed} guestMode={guestMode} />
        </div>
      </div>
      <div className="md:hidden h-[calc(64px+env(safe-area-inset-top,0px))] flex items-center justify-between px-4 pt-[env(safe-area-inset-top,0px)] w-full">
        <NavbarLogo
          enableDebug={enableNavbarDebug}
          showNativeDevLabel={showNativeDevLabel}
          onLogoIconClick={handleLogoClick}
        />
        <div
          className="cursor-pointer w-10 h-10 flex items-center justify-center relative"
          onClick={() => setMenuOpen(true)}
        >
          <MenuIcon className="w-6 h-6 text-[#44403C]" />
          {!guestMode && timUnreadCount > 0 && (
            <div className="absolute right-[-8px] top-0 min-w-[18px] h-[18px] rounded-full bg-[#ff4d4f] px-1 text-white text-[12px] flex items-center justify-center">
              {timUnreadCount > 99 ? '99+' : timUnreadCount}
            </div>
          )}
        </div>
      </div>
      <Drawer
        className="creator-menu-drawer-root"
        open={menuOpen}
        anchor={'bottom'}
        onClose={() => setMenuOpen(false)}
        hideBackdrop
        classes={{
          paper: 'creator-menu-drawer-paper'
        }}
        PaperProps={{
          // bottom anchor 默认 top:auto + maxHeight:100%，在 iOS 上高度易算短，顶部会露出遮罩（灰条）
          style: {
            boxShadow: 'none',
            padding:
              '0px 16px calc(16px + env(safe-area-inset-bottom, 0px)) 16px',
            backgroundColor: '#fff',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            height: '100%',
            maxHeight: 'none'
          }
        }}
      >
        <div className="flex flex-col h-full min-h-0 pt-[env(safe-area-inset-top,0px)]">
          <div className="h-[64px] flex-shrink-0 flex items-center justify-between w-full">
            <NavbarLogo
              enableDebug={enableNavbarDebug}
              showNativeDevLabel={showNativeDevLabel}
              onLogoIconClick={handleLogoClick}
            />
            <div
              className="cursor-pointer w-10 h-10 flex items-center justify-center"
              onClick={() => setMenuOpen(false)}
            >
              <CloseIcon className="w-4 h-4 text-[#44403C]" />
            </div>
          </div>
          <NavbarContent setMenuOpen={setMenuOpen} guestMode={guestMode} />
        </div>
      </Drawer>
      <ModalBox />
    </>
  );
}
